import { Link } from "react-router-dom";
import { useUiLang } from "../../../stores/uiLang";

function DataProcessingAgreementPageEn() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Data Processing Agreement</h1>
        <p className="text-lg max-w-2xl mx-auto">
          SDPC National Data Privacy Agreement (NDPA v2.2) — available for
          execution by contracting school districts.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="text-xs text-gray-500">
          <strong>Framework:</strong> SDPC NDPA v2.2 (Student Data Privacy Consortium) &nbsp;|&nbsp;
          <strong>Operator:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Contact:</strong>{" "}
          <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
            privacy@startupsmartup.com
          </a>
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">What Is the NDPA?</h2>
          <p className="mb-3">
            The Student Data Privacy Consortium's National Data Privacy
            Agreement (SDPC NDPA v2.2) is a standardized data processing
            agreement used by K-12 school districts across the United States to
            establish clear, enforceable privacy commitments with educational
            technology providers.
          </p>
          <p>
            Startup Smartup has pre-filled the provider-side fields of the SDPC
            NDPA v2.2 for Project Explore. Districts execute the agreement by
            completing the district-specific fields (district name, signatory,
            execution date) and countersigning.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">Key Commitments</h2>
          <p className="mb-3">
            The executed NDPA establishes binding commitments including:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Data elements:</strong> Explicit list of student data collected (see Exhibit B) — matches our <Link to="/privacy" className="text-indigo-600 underline">Privacy Policy</Link> data table</li>
            <li><strong>Retention:</strong> Active student data retained for enrollment period + 90 days; teacher data 365 days after last activity; audit logs 7 years</li>
            <li><strong>Security:</strong> AES-256 encryption at rest, TLS 1.2+ in transit, Row Level Security access controls, annual incident response exercise</li>
            <li><strong>Cyber liability:</strong> Provider will bind coverage with limits no less than $1,000,000 per occurrence and $2,000,000 aggregate prior to the effective date of the Agreement</li>
            <li><strong>Sub-processors:</strong> Disclosed in Exhibit H; district notification of material changes</li>
            <li><strong>Contract termination:</strong> Return or deletion of student data within 30 days; certificate of deletion available</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">Request the Pre-Filled NDPA</h2>
          <p className="mb-3">
            District administrators may request the pre-filled NDPA v2.2 PDF
            for review and execution by contacting:
          </p>
          <p className="mb-4">
            <strong>Justin Ghiglia</strong>
            <br />
            Startup Smartup
            <br />
            2380 California Street #402, San Francisco, CA 94115
            <br />
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>
          </p>
          <p className="text-xs text-gray-500">
            The pre-filled NDPA PDF covers all provider-constant fields.
            District-specific fields (district name, signatory name and title,
            execution date) are left blank for per-deal completion.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">Related Compliance Documents</h2>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><Link to="/privacy" className="text-indigo-600 underline">Privacy Policy</Link> — full data collection and use disclosure</li>
            <li><Link to="/privacy#coppa" className="text-indigo-600 underline">COPPA Addendum</Link> — children's privacy compliance</li>
            <li><Link to="/ferpa" className="text-indigo-600 underline">FERPA Statement</Link> — education records compliance</li>
            <li><Link to="/legal/sopipa" className="text-indigo-600 underline">California SOPIPA Acknowledgment</Link> — state-level student data privacy</li>
            <li><Link to="/legal/incident-response" className="text-indigo-600 underline">Incident Response Policy</Link> — breach notification procedures</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

function DataProcessingAgreementPageEs() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Acuerdo de procesamiento de datos</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Acuerdo Nacional de Privacidad de Datos del SDPC (NDPA v2.2),
          disponible para que lo firmen los distritos escolares contratantes.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="p-3 bg-indigo-50 border-l-4 border-indigo-500 rounded text-indigo-800">
          Esta página es una traducción al español. Para ver el texto original en inglés, elige EN arriba.
        </p>
        <p className="text-xs text-gray-500">
          <strong>Marco:</strong> SDPC NDPA v2.2 (Student Data Privacy Consortium) &nbsp;|&nbsp;
          <strong>Operador:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Contacto:</strong>{" "}
          <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
            privacy@startupsmartup.com
          </a>
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">¿Qué es el NDPA?</h2>
          <p className="mb-3">
            El Acuerdo Nacional de Privacidad de Datos (SDPC NDPA v2.2) del
            Student Data Privacy Consortium es un acuerdo de procesamiento de
            datos estandarizado que usan los distritos escolares de K-12 de
            todo Estados Unidos para establecer compromisos de privacidad
            claros y exigibles con los proveedores de tecnología educativa.
          </p>
          <p>
            Startup Smartup ya llenó los campos del lado del proveedor del SDPC
            NDPA v2.2 para Project Explore. Los distritos firman el acuerdo
            completando los campos propios del distrito (nombre del distrito,
            firmante, fecha de firma) y contrafirmándolo.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">Compromisos clave</h2>
          <p className="mb-3">
            El NDPA firmado establece compromisos obligatorios, entre ellos:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Datos recopilados:</strong> Lista explícita de los datos de estudiantes que se recopilan (consulta el Anexo B); coincide con la tabla de datos de nuestra <Link to="/privacy" className="text-indigo-600 underline">Política de privacidad</Link></li>
            <li><strong>Conservación:</strong> Los datos de estudiantes activos se conservan durante el período de inscripción más 90 días; los datos de maestros, 365 días después de su última actividad; los registros de auditoría, 7 años</li>
            <li><strong>Seguridad:</strong> Cifrado AES-256 en reposo, TLS 1.2+ en tránsito, controles de acceso con seguridad a nivel de fila, ejercicio anual de respuesta a incidentes</li>
            <li><strong>Responsabilidad cibernética:</strong> El Proveedor contratará una cobertura con límites no menores de $1,000,000 por evento y $2,000,000 en total antes de la fecha de entrada en vigor del Acuerdo</li>
            <li><strong>Subprocesadores:</strong> Se detallan en el Anexo H; se avisa al distrito de los cambios importantes</li>
            <li><strong>Terminación del contrato:</strong> Devolución o eliminación de los datos de estudiantes en un plazo de 30 días; hay un certificado de eliminación disponible</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">Solicita el NDPA ya llenado</h2>
          <p className="mb-3">
            Los administradores del distrito pueden pedir el PDF del NDPA v2.2
            ya llenado para revisarlo y firmarlo, comunicándose con:
          </p>
          <p className="mb-4">
            <strong>Justin Ghiglia</strong>
            <br />
            Startup Smartup
            <br />
            2380 California Street #402, San Francisco, CA 94115
            <br />
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>
          </p>
          <p className="text-xs text-gray-500">
            El PDF del NDPA ya llenado cubre todos los campos que son
            constantes del proveedor. Los campos propios del distrito (nombre
            del distrito, nombre y cargo del firmante, fecha de firma) se dejan
            en blanco para completarlos en cada contrato.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">Documentos de cumplimiento relacionados</h2>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><Link to="/privacy" className="text-indigo-600 underline">Política de privacidad</Link>: información completa sobre la recopilación y el uso de datos</li>
            <li><Link to="/privacy#coppa" className="text-indigo-600 underline">Anexo de COPPA</Link>: cumplimiento de la privacidad de los niños</li>
            <li><Link to="/ferpa" className="text-indigo-600 underline">Declaración de FERPA</Link>: cumplimiento sobre los expedientes educativos</li>
            <li><Link to="/legal/sopipa" className="text-indigo-600 underline">Reconocimiento de SOPIPA de California</Link>: privacidad de datos de estudiantes a nivel estatal</li>
            <li><Link to="/legal/incident-response" className="text-indigo-600 underline">Política de respuesta a incidentes</Link>: procedimientos de notificación de filtraciones</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

export default function DataProcessingAgreementPage() {
  return useUiLang() === "es" ? <DataProcessingAgreementPageEs /> : <DataProcessingAgreementPageEn />;
}
